import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Checkbox,
  IconButton,
  Typography,
  Alert,
  FormControl,
  RadioGroup,
  Radio,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import CustomerSearch from '../common/CustomerSearch'
import { Close } from '@mui/icons-material'
import { authedAxios } from '../utils'
import useAlert from '../hooks/useAlert'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import useCustomer from '../hooks/api/useCustomer'

const QuickNote = () => {
  const { pathname, search } = useLocation()
  const isUnpaid =
    pathname.includes('/billing') && search.includes('unpaid=true')

  const [open, setOpen] = useState(false)

  const [note, setNote] = useState('')

  const [noteDestination, setNoteDestination] = useState('NEW')

  const [newCustomerName, setNewCustomerName] = useState('')

  const [customerSearchQuery, setCustomerSearchQuery] = useState('')

  const [selectedCustomer, setSelectedCustomer] = useState(null)

  const [errorSaving, setErrorSaving] = useState(false)
  const [duplicateName, setDuplicateName] = useState(false)

  const location = useLocation()

  const { id: queryParamId } = useParams()

  const { customer: customerFromURL } = useCustomer(queryParamId, {
    skip: !queryParamId,
  })

  const hasCurrentCustomer =
    location.pathname.includes('/customers/') && !!queryParamId

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer)
    setCustomerSearchQuery('')
  }

  const { createAlert } = useAlert()

  useEffect(() => {
    setDuplicateName(false)
    setErrorSaving(false)
    setNoteDestination('NEW')
  }, [open])

  const navigate = useNavigate()

  const resetAndAlert = (id) => {
    createAlert({
      close: true,
      message: 'Note created successfully',
      action:
        noteDestination === 'CURRENT'
          ? null
          : {
              label: 'View Profile',
              onClick: () => {
                navigate(`/customers/${id}`)
              },
              timeout: 20000,
            },
    })
    setOpen(false)
    setNote('')
    setNewCustomerName('')
    setCustomerSearchQuery('')
    setSelectedCustomer(null)

    setDuplicateName(false)
    setErrorSaving(false)
  }

  const saveNote = (id) => {
    authedAxios
      .post(`/api/customer/${id}/note`, { note })
      .then(() => {
        resetAndAlert(id)
      })

      .catch(() => setErrorSaving(true))
  }

  const handleSave = () => {
    setErrorSaving(false)
    setDuplicateName(false)

    if (noteDestination === 'NEW' && newCustomerName) {
      return authedAxios
        .post(`/api/customer`, { name: newCustomerName })

        .then((res) => {
          const id = res.data.customer.id

          if (
            res.data.customer.success === false &&
            res.data.customer.error === 'DUPLICATE_NAME'
          ) {
            return setDuplicateName(true)
          }

          return saveNote(id).then(() => {
            // navigate(`/customer/${id}`)
            resetAndAlert(id)
          })
        })
        .catch(() => setErrorSaving(true))
    }

    const id =
      noteDestination === 'EXISTING' ? selectedCustomer.id : queryParamId

    return saveNote(id)
  }

  const saveDisabled =
    !note ||
    (noteDestination === 'EXISTING' && !selectedCustomer) ||
    (noteDestination === 'NEW' && !newCustomerName)

  if (isUnpaid) return null

  return (
    <>
      <Box>
        <Button sx={{ fontSize: '16px' }} onClick={() => setOpen(true)}>
          Quick Note
        </Button>
      </Box>

      <Dialog open={open} maxWidth='md' fullWidth>
        <DialogTitle>Quick Note</DialogTitle>
        <DialogContent>
          <FormControl>
            <RadioGroup
              value={noteDestination}
              onChange={(e) => setNoteDestination(e.target.value)}
            >
              <FormControlLabel
                value={'NEW'}
                control={<Radio />}
                label={'Save a to New Account'}
              />

              <FormControlLabel
                value={'EXISTING'}
                control={<Radio />}
                label={'Save to an Existing Account'}
              />

              {hasCurrentCustomer && (
                <FormControlLabel
                  value={'CURRENT'}
                  control={<Radio />}
                  label={`Save to the Current Account (${customerFromURL?.name})`}
                />
              )}
            </RadioGroup>
          </FormControl>

          {noteDestination === 'EXISTING' && (
            <Box>
              {!selectedCustomer && open && (
                <CustomerSearch
                  margin={'dense'}
                  searchQuery={customerSearchQuery}
                  setSearchQuery={setCustomerSearchQuery}
                  // value={selectedCustomer}
                  allowAdd={false}
                  onSelect={handleSelectCustomer}
                  closeOnSelect
                />
              )}
              {selectedCustomer && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography>
                    Account: <b>{selectedCustomer.name}</b>
                  </Typography>
                  <IconButton onClick={() => setSelectedCustomer(null)}>
                    <Close />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}

          {noteDestination === 'NEW' && (
            <TextField
              label={'New Account Name'}
              fullWidth
              margin='dense'
              value={newCustomerName}
              onChange={(e) => setNewCustomerName(e.target.value)}
            ></TextField>
          )}

          <TextField
            autoFocus
            label='Note'
            fullWidth
            margin='dense'
            multiline
            minRows={3}
            maxRows={10}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          ></TextField>

          <Box sx={{ mt: 2 }}>
            {errorSaving && (
              <Alert severity='error'>
                Something went wrong trying to save this note
              </Alert>
            )}
            {duplicateName && (
              <Alert severity='warning'>
                An account with that name already exists
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            disabled={saveDisabled}
            variant={'contained'}
            onClick={handleSave}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default QuickNote
